import React, { useEffect, useRef, useState } from "react";
import { Skeleton } from "antd";
import { ASSETS_BASE_URL } from "../../../config/constants";
import { DEFAULT_COUPON_CONTENT } from "./couponContentDefaults";

export const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${ASSETS_BASE_URL}${path.replace(/^\//, "")}`;
};

export const COUPON_LAYOUT_WIDTH = 1050;
const PAGE_HORIZONTAL_PADDING = 32;

export const pageStyle = {
  margin: 0,
  padding: "24px 16px",
  minHeight: "100vh",
  backgroundColor: "#d5d5d5",
  boxSizing: "border-box",
  fontFamily: "Arial, Helvetica, sans-serif",
  overflowX: "hidden",
};

const cardStyle = {
  width: `${COUPON_LAYOUT_WIDTH}px`,
  margin: "0 auto",
  display: "flex",
  flexShrink: 0,
  minHeight: "480px",
  boxShadow: "0 6px 28px rgba(0,0,0,0.3)",
  overflow: "hidden",
};

export const ScaledCouponLayout = ({ children }) => {
  const contentRef = useRef(null);
  const [layoutScale, setLayoutScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const updateLayoutScale = () => {
      const availableWidth = window.innerWidth - PAGE_HORIZONTAL_PADDING;
      setLayoutScale(Math.min(1, availableWidth / COUPON_LAYOUT_WIDTH));
    };

    updateLayoutScale();
    window.addEventListener("resize", updateLayoutScale);
    return () => window.removeEventListener("resize", updateLayoutScale);
  }, []);

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return undefined;

    const updateContentHeight = () => {
      setContentHeight(node.offsetHeight);
    };

    updateContentHeight();

    const observer = new ResizeObserver(updateContentHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div
      style={{
        width: COUPON_LAYOUT_WIDTH * layoutScale,
        height: contentHeight ? contentHeight * layoutScale : "auto",
        margin: "0 auto",
        overflow: "hidden",
      }}
    >
      <div
        ref={contentRef}
        style={{
          width: COUPON_LAYOUT_WIDTH,
          transform: `scale(${layoutScale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const CouponSkeleton = () => (
  <div style={pageStyle}>
    <ScaledCouponLayout>
      <div style={cardStyle}>
        <div
          style={{
            flex: "0 0 28%",
            backgroundColor: "#ffffff",
            borderRight: "2px dashed #888",
            padding: "18px 16px 14px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "26px",
              gap: "6px",
            }}
          >
            <Skeleton.Image active style={{ width: 75, height: 50 }} />
            <Skeleton.Button active size="small" style={{ width: 95, height: 32 }} />
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
            {[1, 2, 3, 4].map((item) => (
              <div key={item}>
                <Skeleton.Input active size="small" style={{ width: 72, marginBottom: 8, height: 16 }} />
                <Skeleton.Input active block style={{ height: 28 }} />
              </div>
            ))}
          </div>

          <Skeleton.Input active block style={{ height: 28, marginTop: 20 }} />
        </div>

        <div
          style={{
            flex: "1 1 72%",
            display: "flex",
            flexDirection: "column",
            minHeight: "480px",
            backgroundColor: "#1a0508",
          }}
        >
          <div style={{ padding: "14px 16px 0" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "8px",
              }}
            >
              <Skeleton.Image active style={{ width: 80, height: 50 }} />
              <Skeleton.Button active size="small" style={{ width: 95, height: 32 }} />
            </div>
          </div>

          <div style={{ flex: 1, padding: "8px 16px" }}>
            <Skeleton.Image active style={{ width: "100%", minHeight: 220, display: "block" }} />
          </div>

          <div>
            <div
              style={{
                background: "linear-gradient(180deg, #c41e26 0%, #8f1419 100%)",
                borderTop: "3px solid #ffd700",
                padding: "16px 18px",
              }}
            >
              <Skeleton active paragraph={{ rows: 3 }} title={false} />
            </div>
            <div style={{ backgroundColor: "#0a0a0a", padding: "10px 16px" }}>
              <Skeleton.Input active block style={{ height: 14, backgroundColor: "rgba(255,255,255,0.08)" }} />
            </div>
          </div>
        </div>
      </div>
    </ScaledCouponLayout>
  </div>
);

const fieldLine = (value) => ({
  borderBottom: "2px solid #333",
  minHeight: "28px",
  fontSize: "14px",
  color: "#111",
  paddingBottom: "2px",
  letterSpacing: value ? "normal" : "0.5px",
});

/**
 * Presentational Buy & Win raffle coupon card (UI only — no PDF logic).
 */
const BuyWinCouponCard = ({
  order_no = "N/A",
  name = "",
  cpr = "N/A",
  mob = "",
  raffleCoupon = "",
  drawDateFormatted = "N/A",
  promotionFrom = "",
  promotionTo = "",
  couponContent = DEFAULT_COUPON_CONTENT,
  wrapWithScaledLayout = true,
}) => {
  const logoImageSrc = resolveImageUrl(couponContent.logo_image);
  const raffleImageSrc = resolveImageUrl(couponContent.raffle_background_image);

  const card = (
    <div style={cardStyle}>
      {/* Left tear-off stub */}
      <div
        style={{
          flex: "0 0 28%",
          flexShrink: 0,
          backgroundColor: "#ffffff",
          borderRight: "2px dashed #888",
          padding: "18px 16px 14px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "26px",
            gap: "6px",
          }}
        >
          <img
            src={logoImageSrc}
            alt={couponContent.logo_alt}
            style={{ width: "75px", height: "auto", flexShrink: 0 }}
          />
          <div
            style={{
              border: "2px solid #222",
              borderRadius: "10px",
              padding: "5px 10px",
              fontSize: "12px",
              fontWeight: "700",
              color: "#222",
            }}
          >
            SL.NO : {order_no}
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Name :</div>
            <div style={fieldLine(name)}>{name}</div>
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>C.P.R :</div>
            <div style={fieldLine(cpr)}>{cpr}</div>
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Mob :</div>
            <div style={fieldLine(mob)}>{mob}</div>
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Raffle Coupon :</div>
            <div style={fieldLine(raffleCoupon)}>{raffleCoupon}</div>
          </div>
          <div>
            <div style={{ fontSize: "14px", fontWeight: "700", marginBottom: "5px" }}>Draw Date :</div>
            <div style={fieldLine(drawDateFormatted)}>{drawDateFormatted}</div>
          </div>
        </div>

        <p
          style={{
            margin: "20px 0 0",
            fontSize: "10px",
            color: "#333",
            lineHeight: 1.35,
            textAlign: "center",
          }}
        >
          {couponContent.promo_text}
        </p>
      </div>

      {/* Right promotional body */}
      <div
        style={{
          flex: "1 1 72%",
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: "480px",
        }}
      >
        <img
          src={raffleImageSrc}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "65%",
            objectFit: "cover",
            objectPosition: "center 32%",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: "inherit",
            padding: "14px 16px 0",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "8px",
              flexShrink: 0,
            }}
          >
            <img src={logoImageSrc} alt={couponContent.logo_alt} style={{ width: "80px" }} />
            <div
              style={{
                border: "2px solid #fff",
                borderRadius: "10px",
                padding: "5px 12px",
                fontSize: "12px",
                fontWeight: "700",
                color: "#fff",
                backgroundColor: "rgba(0,0,0,0.35)",
              }}
            >
              SL.NO : {order_no}
            </div>
          </div>

          <div style={{ flex: 1 }} />

          <div
            style={{
              flexShrink: 0,
              marginLeft: "-16px",
              marginRight: "-16px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(180deg, #c41e26 0%, #8f1419 100%)",
                borderTop: "3px solid #ffd700",
                padding: "16px 18px",
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.1fr) auto minmax(0, 1.2fr)",
                gap: "18px",
                alignItems: "center",
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#fff",
                  lineHeight: 1.5,
                  textShadow: "0 1px 2px rgba(0,0,0,0.25)",
                }}
              >
                {couponContent.promo_text}
              </p>

              <div
                style={{
                  textAlign: "center",
                  padding: "0 18px",
                  borderLeft: "1px solid rgba(255,215,0,0.35)",
                  borderRight: "1px solid rgba(255,215,0,0.35)",
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "900",
                    color: "#ffd700",
                    letterSpacing: "1.5px",
                    lineHeight: 1.2,
                  }}
                >
                  {couponContent.brand_title}
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#fff",
                    letterSpacing: "2px",
                    marginTop: "2px",
                  }}
                >
                  {couponContent.brand_subtitle}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "rgba(0,0,0,0.28)",
                  border: "1px solid rgba(255,215,0,0.2)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "10px",
                  color: "#f5f5f5",
                  lineHeight: 1.5,
                }}
              >
                {drawDateFormatted !== "N/A" && (
                  <div
                    style={{
                      display: "inline-block",
                      backgroundColor: "#5c1010",
                      border: "1px solid rgba(255,215,0,0.45)",
                      borderRadius: "20px",
                      padding: "4px 12px",
                      marginBottom: "8px",
                      fontWeight: "700",
                      fontSize: "10px",
                      color: "#ffd700",
                      letterSpacing: "0.4px",
                    }}
                  >
                    DRAW: {drawDateFormatted}
                  </div>
                )}
                <div
                  style={{
                    fontWeight: "700",
                    fontSize: "10px",
                    color: "#ffd700",
                    marginBottom: "5px",
                    letterSpacing: "0.3px",
                  }}
                >
                  {couponContent.terms_title}
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "16px",
                    listStyleType: "disc",
                  }}
                >
                  {(couponContent.terms_items || []).map((term, index) => (
                    <li key={index} style={{ marginBottom: "2px" }}>{term}</li>
                  ))}
                  {couponContent.show_promotion_dates === "Y" && promotionFrom && promotionTo && (
                    <li style={{ marginBottom: "2px" }}>
                      {couponContent.promotion_label}: {promotionFrom} to {promotionTo}
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#0a0a0a",
                color: "#e8e8e8",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px 20px",
                fontSize: "10px",
                padding: "8px 16px",
                letterSpacing: "0.2px",
                borderTop: "1px solid rgba(255,215,0,0.15)",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    color: "#ffd700",
                    fontWeight: "700",
                    fontSize: "9px",
                    letterSpacing: "0.8px",
                  }}
                >
                  MOB
                </span>
                {couponContent.contact_mobile}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    color: "#ffd700",
                    fontWeight: "700",
                    fontSize: "9px",
                    letterSpacing: "0.8px",
                  }}
                >
                  LOCATION
                </span>
                {couponContent.contact_location}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            right: "4px",
            top: "50%",
            transform: "translateY(-50%) rotate(90deg)",
            fontSize: "8px",
            color: "rgba(255,255,255,0.5)",
            whiteSpace: "nowrap",
            zIndex: 2,
          }}
        >
          {couponContent.side_note}
        </div>
      </div>
    </div>
  );

  if (!wrapWithScaledLayout) return card;
  return <ScaledCouponLayout>{card}</ScaledCouponLayout>;
};

export default BuyWinCouponCard;
